import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Card, CardContent, CardHeader } from './ui/card';
import { CalendarIcon, CancelIcon, EditIcon, MailIcon, SaveIcon, UserIcon } from './ui/icons';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { User } from '../types/User.type';
import { Input } from './ui/input';
import { GetUserDashboardSummaryResponse, GetUserDashboardProfileResponse } from '../types/User.api.type';
import { userApi } from '../api/user.api';
// useAuth import removed: refreshUser not available in current AuthContext
import GenderRegisterModal from './modal/GenderRegisterModal';
import BirthdateRegisterModal from './modal/BirthdateRegisterModal';
import ProfileImageModal from './modal/ProfileImageModal';

interface MyPageProps {
  user: User;
  summary: GetUserDashboardSummaryResponse;
}

/**
 * 내 정보 컴포넌트
 *
 * @author
 * @constructor
 */
/** 닉네임 유효성 검증: 2~10자, 한글/영문/숫자만 */
const NICKNAME_REGEX = /^[가-힣a-zA-Z0-9]{2,10}$/;
const validateNickname = (value: string): string | null => {
  if (value.length < 2) return '2자 이상 입력해주세요.';
  if (value.length > 10) return '10자 이하로 입력해주세요.';
  if (!NICKNAME_REGEX.test(value)) return '한글, 영문, 숫자만 사용할 수 있어요.';
  return null;
};

const MyPage = ({ user, summary }: MyPageProps) => {
  /**
   * Hooks
   */
  const queryClient = useQueryClient();

  /**
   * States
   */
  /* 편집모드 제어 state */
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [genderModalOpen, setGenderModalOpen] = useState(false);
  const [birthdateModalOpen, setBirthdateModalOpen] = useState(false);
  const [profileImageModalOpen, setProfileImageModalOpen] = useState(false);

  /** S6-4: 닉네임 편집 */
  const [nicknameValue, setNicknameValue] = useState(user.nickname);
  const [nicknameError, setNicknameError] = useState<string | null>(null);
  const [isNicknameChecking, setIsNicknameChecking] = useState(false);

  /**
   * APIs
   */
  const { data: profileData } = useQuery<GetUserDashboardProfileResponse>({
    queryKey: ['getDashboardProfile'],
    queryFn: () => userApi.getDashboardProfile().then((res) => res.data),
  });

  const genderLabel = profileData?.gender === 'MALE' ? '남성' : profileData?.gender === 'FEMALE' ? '여성' : '등록 시 조각 1개 지급';
  const birthdateLabel = profileData?.birthdate ? profileData.birthdate.replace(/-/g, '.') : '등록 시 조각 1개 지급';

  /** S6-4: 닉네임 input onChange */
  const handleNicknameChange = (value: string) => {
    setNicknameValue(value);
    setNicknameError(validateNickname(value));
  };

  /** S6-4: 닉네임 저장 (checkNickname -> updateNickname) */
  const handleNicknameSave = async () => {
    const error = validateNickname(nicknameValue);
    if (error) {
      setNicknameError(error);
      return;
    }
    if (nicknameValue === user.nickname) {
      setIsEditing(false);
      return;
    }
    setIsNicknameChecking(true);
    try {
      // 중복 체크
      const checkRes = await userApi.checkNickname(nicknameValue);
      const available = checkRes.data.results?.available ?? checkRes.data?.available;
      if (!available) {
        setNicknameError('이미 사용 중인 닉네임이에요.');
        return;
      }
      // 닉네임 변경
      await userApi.updateNickname(nicknameValue);
      toast.success('닉네임이 변경되었어요.');
      setIsEditing(false);
      setNicknameError(null);
      queryClient.invalidateQueries({ queryKey: ['getDashboardProfile'] });
      queryClient.invalidateQueries({ queryKey: ['getDashboardSummary'] });
    } catch (err: any) {
      const message = err?.response?.data?.message || '닉네임 변경에 실패했어요.';
      toast.error(message);
    } finally {
      setIsNicknameChecking(false);
    }
  };

  const handleProfileRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['getDashboardProfile'] });
  };
  return (
    <div className="max-w-4xl mx-auto px-1 py-2 space-y-6">
      {/* 프로필 헤더 */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div
                className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center cursor-pointer hover:ring-2 hover:ring-primary/50 transition-all"
                onClick={() => setProfileImageModalOpen(true)}
              >
                {profileData?.profileImage ? (
                  <img src={profileData.profileImage} alt="profile" className="w-16 h-16 rounded-full object-cover" />
                ) : (
                  <UserIcon className="w-8 h-8 text-primary" />
                )}
              </div>
              <div>
                <h1 className="text-2xl font-bold">{user.nickname}</h1>
                <p className="text-muted-foreground">@{user.nickname}</p>
                <div className="flex items-center gap-2 mt-1">
                  {/* TODO (구현필요) (20250711/x) 활동, 레벨별 다른 다지인 적용 -hjkim */}
                  <Badge className="text-white">Label</Badge>
                  <span className="text-sm text-muted-foreground">1개월 전에 가입</span>
                </div>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {isEditing ? (
            // 편집모드
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">표시 이름</label>
                <Input
                  placeholder="2~10자, 한글/영문/숫자"
                  maxLength={10}
                  value={nicknameValue}
                  onChange={(e) => handleNicknameChange(e.target.value)}
                />
                {nicknameError && (
                  <p className="text-xs text-red-500 mt-1">{nicknameError}</p>
                )}
                <p className="text-xs text-muted-foreground mt-1">{nicknameValue.length}/10자</p>
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  onClick={handleNicknameSave}
                  disabled={!!nicknameError || isNicknameChecking}
                >
                  <SaveIcon className="w-4 h-4 mr-2" />
                  {isNicknameChecking ? '확인 중...' : '저장'}
                </Button>
                <Button variant="outline" onClick={() => {
                  setIsEditing(false);
                  setNicknameValue(user.nickname);
                  setNicknameError(null);
                }}>
                  <CancelIcon className="w-4 h-4 mr-2" />
                  취소
                </Button>
              </div>
            </div>
          ) : (
            // 보기 모드
            <div className="space-y-4">
              <div className="flex items-center justify-center w-full mt-4">
                <Button
                  variant={isEditing ? 'ghost' : 'outline'}
                  className="w-full"
                  onClick={() => setIsEditing(!isEditing)}>
                  <EditIcon className="w-4 h-4 mr-2" />
                  {isEditing ? '편집 취소' : '프로필 편집'}
                </Button>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <MailIcon className="w-4 h-4" />
                <span>{user.email}</span>
              </div>
              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <UserIcon className="w-4 h-4" />
                  <span>성별: {genderLabel}</span>
                </div>
                {!profileData?.gender && (
                  <Button variant="outline" size="sm" onClick={() => setGenderModalOpen(true)}>
                    등록
                  </Button>
                )}
              </div>
              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <CalendarIcon className="w-4 h-4" />
                  <span>생년월일: {birthdateLabel}</span>
                </div>
                {!profileData?.birthdate && (
                  <Button variant="outline" size="sm" onClick={() => setBirthdateModalOpen(true)}>
                    등록
                  </Button>
                )}
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <CalendarIcon className="w-4 h-4" />
                <span>마지막 활동: 12일 전</span>
              </div>
              {/*<div>*/}
              {/*  <h3 className="font-medium mb-2">자기소개</h3>*/}
              {/*  <p className="text-muted-foreground">하이루!!</p>*/}
              {/*</div>*/}
            </div>
          )}
        </CardContent>
      </Card>
      {/* 활동 통계 */}
      <Card>
        <CardHeader>
          <h2 className="text-xl font-semibold mb-2">활동 통계</h2>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-primary">{summary.totalCreatedSurveyCount}</div>
              <div className="text-sm text-muted-foreground">작성한 설문</div>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-primary">{summary.totalParticipatedSurveyCount}</div>
              <div className="text-sm text-muted-foreground">참여한 설문</div>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-primary">{summary.totalCommentCount}</div>
              <div className="text-sm text-muted-foreground">작성한 댓글</div>
            </div>
          </div>
        </CardContent>
      </Card>
      {/* 설정 */}
      {/*<Card>*/}
      {/*  <CardHeader>*/}
      {/*    <h2 className="text-xl font-semibold">설정</h2>*/}
      {/*    <CardContent className="space-y-6">*/}
      {/*      {isEditing ? (*/}
      {/*        <div className="space-y-4">*/}
      {/*          <div className="flex items-center justify-between">*/}
      {/*            <div>*/}
      {/*              <h3 className="font-medium">이메일 알림</h3>*/}
      {/*              <p className="text-sm text-muted-foreground">*/}
      {/*                새로운 댓글이나 반응이 있을 때 이메일로 알림을 받습니다.*/}
      {/*              </p>*/}
      {/*            </div>*/}
      {/*            <Switch />*/}
      {/*          </div>*/}
      {/*          <Separator />*/}
      {/*          <div className="flex items-center justify-between">*/}
      {/*            <div>*/}
      {/*              <h3 className="font-medium">이메일 공개</h3>*/}
      {/*              <p className="text-sm text-muted-foreground">*/}
      {/*                다른 사용자에게 이메일 주소를 공개합니다.*/}
      {/*              </p>*/}
      {/*            </div>*/}
      {/*            <Switch />*/}
      {/*          </div>*/}
      {/*        </div>*/}
      {/*      ) : (*/}
      {/*        <div className="space-y-4">*/}
      {/*          <div className="flex items-center justify-between">*/}
      {/*            <span>이메일 알림</span>*/}
      {/*            <Badge variant="default">ON</Badge>*/}
      {/*          </div>*/}
      {/*          <div className="flex items-center justify-between">*/}
      {/*            <span>이메일 공개</span>*/}
      {/*            <Badge variant="secondary">OFF</Badge>*/}
      {/*          </div>*/}
      {/*        </div>*/}
      {/*      )}*/}
      {/*    </CardContent>*/}
      {/*  </CardHeader>*/}
      {/*</Card>*/}

      {/* 모달 */}
      <GenderRegisterModal
        isOpen={genderModalOpen}
        onClose={() => setGenderModalOpen(false)}
        onSuccess={handleProfileRefresh}
      />
      <BirthdateRegisterModal
        isOpen={birthdateModalOpen}
        onClose={() => setBirthdateModalOpen(false)}
        onSuccess={handleProfileRefresh}
      />
      <ProfileImageModal
        isOpen={profileImageModalOpen}
        onClose={() => setProfileImageModalOpen(false)}
        onSuccess={handleProfileRefresh}
        currentImageUrl={profileData?.profileImage}
      />
    </div>
  );
};

export default MyPage;
